/**
 * Innates Middleware Service
 * Handles communication with Kapitani Middleware for user/farm management
 * 
 * API Documentation: Based on Middleware-Backend IE V2.pdf
 * 
 * APIs Implemented:
 * - API 1: Farmer Register (POST /register-user) ✅ Integrated
 * - API 2: Farm Register (POST /register-farm) ✅ Integrated
 * - API 3: Farmer Info Update (PATCH /edit-user) ✅ Integrated
 * - API 4: Farm Info Update (PATCH /edit-farm) ✅ Implemented
 * - API 5: Farmer Delete (POST /delete-user) ✅ Implemented
 * - API 6: Delete Farm (POST /delete-farm) ✅ Implemented
 * - API 7: Subscribed Vendor (POST /vendor-subscribe) ✅ Implemented
 * - API 10: Get All Registered Vendor (GET /vendor-list) ✅ Implemented
 * - API 11: Post iTraceability Data (POST /harvest) ✅ Implemented
 * - API 12: Get iTraceability QR Code (GET /harvest) ✅ Implemented
 * - API 13: Get List of User/Farmer (GET /list-user) ✅ via /api/middleware-lookup
 * - API 14: Get List of Farm (GET /list-farm) ✅ via /api/middleware-lookup
 * 
 * APIs Not Implemented (Webhooks - require server-side endpoint):
 * - API 8: Post Every Alert (Middleware → IE)
 * - API 9: Post New Registered Vendor (Middleware → IE)
 */

import { KAPITANI_BASE_URL } from '../config/iotVendors';

// Innates Middleware Configuration
const INNATES_CONFIG = {
  baseUrl: KAPITANI_BASE_URL,
  // Client ID for Smart Farm Platform - same as other vendors
  clientId: '7376891376107134976',
  debug: process.env.REACT_APP_DEBUG_INNATES === 'true' || process.env.NODE_ENV === 'development'
};

class InnatesMiddlewareService {
  constructor() {
    this.baseUrl = INNATES_CONFIG.baseUrl;
    this.clientId = INNATES_CONFIG.clientId;
    this.debug = INNATES_CONFIG.debug;
  }

  /**
   * Log debug messages
   */
  log(message, data = null) {
    if (this.debug) {
      if (data) {
        console.log(`🔗 [Innates Middleware] ${message}`, data);
      } else {
        console.log(`🔗 [Innates Middleware] ${message}`);
      }
    }
  }

  /**
   * The middleware reports some failures with HTTP 200 and an error body,
   * e.g. {"status":"fail","message":"Missing 'id'"} or
   * {"code":"ENTITY_NOT_FOUND",...} - treat those as errors, not as success.
   */
  assertSuccess(result, action) {
    const failed =
      !result ||
      typeof result !== 'object' ||
      result.code ||
      result.error ||
      String(result.status || '').toLowerCase() === 'fail';
    if (failed) {
      const reason = result?.message || result?.sqlMessage || result?.code || result?.error?.message || 'unexpected reply';
      throw new Error(`${action} was not accepted by the middleware: ${reason}`);
    }
    return result;
  }

  /**
   * API 13 / API 14: find the middleware record of one farmer or one farm.
   * The lists are read on our server (/api/middleware-lookup) because the
   * farmer list holds phone and IC numbers; only the record's ids come back.
   *
   * @param {'user'|'farm'} type
   * @param {string|number} id - our user id or farm id
   * @returns {Promise<{found: boolean, id?: string, farmerId?: string|null, isDeleted?: boolean}|null>}
   *          null when the lookup itself is not available
   */
  async lookupRecord(type, id) {
    try {
      const response = await fetch(`/api/middleware-lookup?type=${type}&id=${encodeURIComponent(String(id))}`);
      const contentType = (response.headers.get('content-type') || '').toLowerCase();
      if (!response.ok || !contentType.includes('application/json')) {
        this.log(`Lookup ${type} ${id} not available (HTTP ${response.status})`);
        return null;
      }
      return await response.json();
    } catch (error) {
      this.log(`Lookup ${type} ${id} failed: ${error.message}`);
      return null;
    }
  }

  /**
   * API 1: Register Farmer
   * Sends newly registered farmer data to the middleware
   * 
   * Endpoint: POST /client/{clientId}/register-user
   * 
   * @param {Object} farmerData - Farmer registration data
   * @param {string} farmerData.farmerId - Unique farmer identifier
   * @param {string} farmerData.name - Farmer's full name
   * @param {string} farmerData.phone - Phone number
   * @param {string} farmerData.icNo - IC number (format: 000000-00-0000)
   * @returns {Promise<{status: string, id: string}>}
   */
  async registerFarmer(farmerData) {
    try {
      this.log('Registering farmer:', farmerData.name);
      
      const payload = {
        farmerId: farmerData.farmerId,
        name: farmerData.name,
        phone: farmerData.phone,
        icNo: farmerData.icNo
      };

      this.log('Payload:', payload);

      const url = `${this.baseUrl}/client/${this.clientId}/register-user`;
      this.log('Endpoint:', url);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      this.log('Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText || response.statusText}`);
      }

      const result = await response.json();
      this.log('Response:', result);

      return result;
    } catch (error) {
      console.error('❌ [Innates Middleware] Error registering farmer:', error);
      throw error;
    }
  }

  /**
   * API 2: Register Farm
   * Sends newly registered farm data to the middleware
   * 
   * Endpoint: POST /client/{clientId}/register-farm
   * 
   * @param {Object} farmData - Farm registration data
   * @param {string} farmData.farmerId - Owner's farmer ID
   * @param {string} farmData.farmId - Unique farm identifier
   * @param {string} farmData.location - Location coordinates (format: "lat,long")
   * @returns {Promise<{status: string, id: string}>}
   */
  async registerFarm(farmData) {
    try {
      this.log('Registering farm:', farmData.farmId);

      const payload = {
        farmerId: farmData.farmerId,
        farmId: farmData.farmId,
        location: farmData.location
      };

      this.log('Payload:', payload);

      const url = `${this.baseUrl}/client/${this.clientId}/register-farm`;
      this.log('Endpoint:', url);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      this.log('Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText || response.statusText}`);
      }

      const result = await response.json();
      this.log('Response:', result);

      return result;
    } catch (error) {
      console.error('❌ [Innates Middleware] Error registering farm:', error);
      throw error;
    }
  }

  /**
   * API 3: Update Farmer Info
   * Sends updated farmer information to the middleware
   * 
   * Endpoint: PATCH /client/{clientId}/edit-user
   * 
   * @param {Object} farmerData - Updated farmer data
   * @param {string} farmerData.farmerId - Farmer identifier
   * @param {string} farmerData.name - Updated name
   * @param {string} farmerData.phone - Updated phone
   * @param {string} farmerData.icNo - Updated IC number
   * @param {string} farmerData.id - Middleware record ID
   * @returns {Promise<{status: string}>}
   */
  async updateFarmer(farmerData) {
    try {
      this.log('Updating farmer:', farmerData.farmerId);

      const payload = {
        farmerId: farmerData.farmerId,
        name: farmerData.name,
        phone: farmerData.phone,
        icNo: farmerData.icNo,
        id: farmerData.id
      };

      this.log('Payload:', payload);

      const url = `${this.baseUrl}/client/${this.clientId}/edit-user`;
      this.log('Endpoint:', url);

      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      this.log('Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText || response.statusText}`);
      }

      const result = await response.json();
      this.log('Response:', result);

      return this.assertSuccess(result, 'Farmer update');
    } catch (error) {
      console.error('❌ [Innates Middleware] Error updating farmer:', error);
      throw error;
    }
  }

  /**
   * API 4: Update Farm Info
   * Sends updated farm information to the middleware
   * 
   * Endpoint: PATCH /client/{clientId}/edit-farm
   * 
   * @param {Object} farmData - Updated farm data
   * @param {string} farmData.farmerId - Owner's farmer ID
   * @param {string} farmData.farmId - Farm identifier
   * @param {string} farmData.location - Updated location (lat,long)
   * @param {string} farmData.id - Middleware record ID
   * @returns {Promise<{status: string}>}
   */
  async updateFarm(farmData) {
    try {
      this.log('Updating farm:', farmData.farmId);

      const payload = {
        farmerId: farmData.farmerId,
        farmId: farmData.farmId,
        location: farmData.location,
        id: farmData.id
      };

      this.log('Payload:', payload);

      const url = `${this.baseUrl}/client/${this.clientId}/edit-farm`;
      this.log('Endpoint:', url);

      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      this.log('Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText || response.statusText}`);
      }

      const result = await response.json();
      this.log('Response:', result);

      return this.assertSuccess(result, 'Farm update');
    } catch (error) {
      console.error('❌ [Innates Middleware] Error updating farm:', error);
      throw error;
    }
  }

  /**
   * API 5: Delete Farmer
   * Deletes a farmer account from the middleware
   * 
   * Endpoint: POST /client/{clientId}/delete-user
   * 
   * @param {Object} deleteData - Delete request data
   * @param {string} deleteData.farmerId - Farmer identifier
   * @param {string} deleteData.id - Middleware record ID
   * @returns {Promise<{status: string}>}
   */
  async deleteFarmer(deleteData) {
    try {
      this.log('Deleting farmer:', deleteData.farmerId);

      const payload = {
        farmerId: deleteData.farmerId,
        id: deleteData.id
      };

      this.log('Payload:', payload);

      const url = `${this.baseUrl}/client/${this.clientId}/delete-user`;
      this.log('Endpoint:', url);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      this.log('Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText || response.statusText}`);
      }

      const result = await response.json();
      this.log('Response:', result);

      return result;
    } catch (error) {
      console.error('❌ [Innates Middleware] Error deleting farmer:', error);
      throw error;
    }
  }

  /**
   * API 6: Delete Farm
   * Deletes a farm from the middleware
   * 
   * Endpoint: POST /client/{clientId}/delete-farm
   * 
   * @param {Object} deleteData - Delete request data
   * @param {string} deleteData.farmId - Farm identifier
   * @param {string} deleteData.id - Middleware record ID
   * @returns {Promise<{status: string}>}
   */
  async deleteFarm(deleteData) {
    try {
      this.log('Deleting farm:', deleteData.farmId);

      const payload = {
        farmId: deleteData.farmId,
        id: deleteData.id
      };

      this.log('Payload:', payload);

      const url = `${this.baseUrl}/client/${this.clientId}/delete-farm`;
      this.log('Endpoint:', url);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      this.log('Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText || response.statusText}`);
      }

      const result = await response.json();
      this.log('Response:', result);

      return this.assertSuccess(result, 'Farm delete');
    } catch (error) {
      console.error('❌ [Innates Middleware] Error deleting farm:', error);
      throw error;
    }
  }

  /**
   * API 7: Subscribe to Vendors
   * Sends the list of subscribed vendors for a farm to the middleware
   * 
   * Endpoint: POST /client/{clientId}/vendor-subscribe
   * 
   * @param {Object} subscribeData - Subscription data
   * @param {string} subscribeData.farmId - Farm identifier
   * @param {Array<{vendorId: string}>} subscribeData.vendors - List of vendors to subscribe
   * @returns {Promise<{status: string}>}
   */
  async subscribeVendors(subscribeData) {
    try {
      this.log('Subscribing vendors for farm:', subscribeData.farmId);

      const payload = {
        farmId: subscribeData.farmId,
        vendor: subscribeData.vendors
      };

      this.log('Payload:', payload);

      const url = `${this.baseUrl}/client/${this.clientId}/vendor-subscribe`;
      this.log('Endpoint:', url);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      this.log('Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText || response.statusText}`);
      }

      const result = await response.json();
      this.log('Response:', result);

      return result;
    } catch (error) {
      console.error('❌ [Innates Middleware] Error subscribing vendors:', error);
      throw error;
    }
  }

  /**
   * API 10: Get All Registered Vendors
   * Gets all registered vendors from the middleware
   * 
   * Endpoint: GET /client/{clientId}/vendor-list
   * 
   * @returns {Promise<{vendor: Array<{vendorName: string, vendorId: string}>}>}
   */
  async getVendorList() {
    try {
      this.log('Getting vendor list');

      const url = `${this.baseUrl}/client/${this.clientId}/vendor-list`;
      this.log('Endpoint:', url);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      this.log('Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText || response.statusText}`);
      }

      const result = await response.json();
      this.log('Response:', result);

      return result;
    } catch (error) {
      console.error('❌ [Innates Middleware] Error getting vendor list:', error);
      throw error;
    }
  }

  /**
   * API 11: Post iTraceability Data (Harvest)
   * Sends harvest/cycle data to middleware for QR code generation
   * 
   * Endpoint: POST /client/{clientId}/harvest
   * 
   * @param {Object} harvestData - Harvest data for QR generation
   * @param {string} harvestData.farmId - Farm identifier
   * @param {string} harvestData.cropName - Name of the crop
   * @param {string} harvestData.plantStartDate - Planting start date
   * @param {string} harvestData.harvestDate - Harvest date
   * @param {Array<string>} harvestData.fertilizer - List of fertilizers used
   * @param {Array<string>} harvestData.pesticide - List of pesticides used
   * @param {string} harvestData.farmAddress - Farm address
   * @param {string} harvestData.farmPicture - Base64 encoded farm image (optional)
   * @param {string} harvestData.cropPicture - Base64 encoded crop image (optional)
   * @returns {Promise<{qrCode: string}>} - Base64 encoded QR code image
   */
  async postHarvestData(harvestData) {
    try {
      this.log('Posting harvest data for farm:', harvestData.farmId);

      const payload = {
        farmId: harvestData.farmId,
        cropName: harvestData.cropName,
        plantStartDate: harvestData.plantStartDate,
        harvestDate: harvestData.harvestDate,
        fertilizer: harvestData.fertilizer || [],
        pesticide: harvestData.pesticide || [],
        farmAddress: harvestData.farmAddress || '',
        farmPicture: harvestData.farmPicture || '',
        cropPicture: harvestData.cropPicture || ''
      };

      this.log('Payload:', { ...payload, farmPicture: '[BASE64]', cropPicture: '[BASE64]' });

      const url = `${this.baseUrl}/client/${this.clientId}/harvest`;
      this.log('Endpoint:', url);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      this.log('Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText || response.statusText}`);
      }

      const result = await response.json();
      this.log('Response: QR Code received');

      return result;
    } catch (error) {
      console.error('❌ [Innates Middleware] Error posting harvest data:', error);
      throw error;
    }
  }

  /**
   * API 12: Get iTraceability QR Code
   * Gets existing QR code from middleware database
   * 
   * Endpoint: GET /client/{clientId}/harvest
   * 
   * @param {Object} queryData - Query parameters
   * @param {string} queryData.farmId - Farm identifier
   * @param {string} queryData.cropName - Name of the crop
   * @param {string} queryData.harvestDate - Harvest date
   * @returns {Promise<{qrCode: string}>} - Base64 encoded QR code image
   */
  async getHarvestQRCode(queryData) {
    try {
      this.log('Getting harvest QR code for farm:', queryData.farmId);

      const params = new URLSearchParams({
        farmId: queryData.farmId,
        cropName: queryData.cropName,
        harvestDate: queryData.harvestDate
      });

      const url = `${this.baseUrl}/client/${this.clientId}/harvest?${params}`;
      this.log('Endpoint:', url);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      this.log('Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText || response.statusText}`);
      }

      const result = await response.json();
      this.log('Response: QR Code received');

      return result;
    } catch (error) {
      console.error('❌ [Innates Middleware] Error getting harvest QR code:', error);
      throw error;
    }
  }

  /**
   * Verify API connectivity
   * Tests connection to middleware and returns status
   * 
   * @returns {Promise<Object>} - Verification results
   */
  async verifyConnectivity() {
    const results = {
      timestamp: new Date().toISOString(),
      baseUrl: this.baseUrl,
      clientId: this.clientId,
      apis: {}
    };

    // Test vendor-list (GET - safest to test)
    try {
      const startTime = Date.now();
      const vendorResult = await this.getVendorList();
      results.apis.vendorList = {
        status: 'success',
        latency: Date.now() - startTime,
        vendors: vendorResult?.vendor?.length || 0
      };
    } catch (error) {
      results.apis.vendorList = {
        status: 'error',
        error: error.message
      };
    }

    return results;
  }
}

// Export singleton instance
const innatesMiddlewareService = new InnatesMiddlewareService();
export default innatesMiddlewareService;

// Also export the class for testing
export { InnatesMiddlewareService };
